import { REVISION as THREE_REVISION } from 'three';

/**
 * This bridge intentionally targets one exact Three.js private renderer ABI.
 * Keep every `_nodes` / `_bindings` / `_pipelines` access in this module so a
 * Three upgrade fails closed in one place instead of silently dropping a
 * physics kernel from a caller-owned WebGPU command encoder.
 */
export const THREE_WEBGPU_COMPUTE_BRIDGE_REVISION = '183';

/** Regression-only hook; production renderers never define this symbol. */
export const PMAVBD_FORCE_RENDERER_COMPUTE_FALLBACK = Symbol(
  'pmavbd.forceRendererComputeFallback',
);

export type ThreeWebGpuDispatchSize =
  | number
  | readonly [number, number?, number?]
  | { readonly isIndirectStorageBufferAttribute: true };

export type ThreeWebGpuComputeCommand = Readonly<{
  node: any;
  /** Contiguous commands with the same index belong to one renderer compute pass. */
  passIndex: number;
  /** Null/undefined uses node.count, matching WebGPURenderer.compute(). */
  dispatchSize?: ThreeWebGpuDispatchSize | null;
  /**
   * Required when the same ComputeNode occurs more than once. Three stores a
   * node's uniforms in one mutable allocation, so native fusion is safe only
   * when the collector has proved every occurrence uses invariant state.
   */
  uniformStateInvariant?: boolean;
}>;

export type NativeComputePassDescriptorFactory = (
  label: string,
) => GPUComputePassDescriptor;

type DirectDispatch = {
  kind: 'direct';
  x: number;
  y: number;
  z: number;
};

type IndirectDispatch = {
  kind: 'indirect';
  buffer: GPUBuffer;
};

type PreparedDispatch = DirectDispatch | IndirectDispatch;

type PreparedCommand = {
  pipeline: GPUComputePipeline;
  bindGroups: readonly GPUBindGroup[];
  dispatch: PreparedDispatch;
  passIndex: number;
};

/**
 * Reuses only CPU command-record storage. Pipelines, bind groups, uniforms and
 * dispatches are still resolved from Three on every preparation, so cache use
 * cannot retain stale GPU state or change dispatch order.
 */
export type ThreeWebGpuComputePreparationCache = {
  readonly commands: PreparedCommand[];
};

export function createThreeWebGpuComputePreparationCache():
ThreeWebGpuComputePreparationCache {
  return { commands: [] };
}

export type ThreeWebGpuComputePrepareStats = Readonly<{
  commandCount: number;
  sourcePassCount: number;
  nodePreparations: number;
  invariantNodeReuses: number;
}>;

export type PreparedThreeWebGpuCompute = Readonly<{
  ok: true;
  commands: readonly PreparedCommand[];
  stats: ThreeWebGpuComputePrepareStats;
  /** Undo renderer info accounting if command recording is abandoned. */
  rollbackAccounting: () => void;
}>;

export type ThreeWebGpuComputePrepareFailure = Readonly<{
  ok: false;
  reason: string;
}>;

export type ThreeWebGpuComputePrepareResult =
  | PreparedThreeWebGpuCompute
  | ThreeWebGpuComputePrepareFailure;

export type ThreeWebGpuComputePrepareOptions = Readonly<{
  accountRendererCall?: boolean;
  preparationCache?: ThreeWebGpuComputePreparationCache;
}>;

export type ThreeWebGpuComputePrewarmOptions = Readonly<{
  /**
   * Compile this many newly visited nodes concurrently, then yield. A small
   * batch keeps startup responsive without serializing dozens of independent
   * driver compilations; zero compiles the complete set as one batch.
   */
  yieldEveryNodes?: number;
  /** Injectable scheduler used by regression tests and non-window hosts. */
  yieldToMainThread?: () => Promise<void>;
  /** Called after each completed compilation batch. */
  onProgress?: (completedNodes: number, totalNodes: number) => void;
}>;

export type ThreeWebGpuComputePrewarmStats = Readonly<{
  commandCount: number;
  sourcePassCount: number;
  uniqueNodeCount: number;
  alreadyReadyNodes: number;
  asynchronouslyCompiledPipelines: number;
  pendingNodeReuses: number;
  yields: number;
}>;

export type ThreeWebGpuComputePrewarmSuccess = Readonly<{
  ok: true;
  stats: ThreeWebGpuComputePrewarmStats;
}>;

export type ThreeWebGpuComputePrewarmFailure = Readonly<{
  ok: false;
  reason: string;
  stats: ThreeWebGpuComputePrewarmStats;
}>;

export type ThreeWebGpuComputePrewarmResult =
  | ThreeWebGpuComputePrewarmSuccess
  | ThreeWebGpuComputePrewarmFailure;

type RendererAccountingSnapshot = Readonly<{
  calls: number;
  computeCalls: number;
  computeFrameCalls: number;
  renderId: number;
}>;

type AsyncNodePrewarmOutcome = 'ready' | 'compiled' | 'pending-reuse';

/**
 * A ComputeNode can legally outlive a renderer/device replacement. Scope
 * pending work by renderer first so a new backend never awaits an old device.
 */
const pendingNodePrewarms = new WeakMap<object, WeakMap<object, Promise<void>>>();
const interceptedComputePipelineBackends = new WeakSet<object>();

// NodeFrame uses positive renderer call/frame ids. Unique negative ids make
// prewarm updates observable without consuming public accounting or poisoning
// the first real compute update's render/frame de-duplication state.
let nextPrewarmNodeFrameId = -1;

function failureReason(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function privateSurface(renderer: any): {
  backend: any;
  nodes: any;
  bindings: any;
  pipelines: any;
  nodeFrame: any;
} | null {
  if (String(THREE_REVISION) !== THREE_WEBGPU_COMPUTE_BRIDGE_REVISION) return null;
  const backend = renderer?.backend;
  const nodes = renderer?._nodes;
  const bindings = renderer?._bindings;
  const pipelines = renderer?._pipelines;
  const nodeFrame = nodes?.nodeFrame;
  if (
    !backend?.get
    || !nodes?.updateForCompute
    || !bindings?.updateForCompute
    || !bindings?.getForCompute
    || !pipelines?.getForCompute
    || !pipelines?.get
    || !pipelines?.has
    || !nodeFrame
  ) {
    return null;
  }
  return { backend, nodes, bindings, pipelines, nodeFrame };
}

/** Cheap, side-effect-free readiness probe used after Three primes a node. */
export function isThreeWebGpuComputeBridgeReady(
  renderer: any,
  computeNodes: readonly any[],
): boolean {
  const surface = privateSurface(renderer);
  if (!surface || computeNodes.length === 0) return false;
  return computeNodes.every((node) => {
    if (node?.isComputeNode !== true || !surface.pipelines.has(node)) return false;
    const pipelineWrapper = surface.pipelines.get(node)?.pipeline;
    return Boolean(pipelineWrapper && surface.backend.get(pipelineWrapper)?.pipeline);
  });
}

function mutablePrewarmStats(
  commandCount: number,
  sourcePassCount: number,
  uniqueNodeCount: number,
): {
  commandCount: number;
  sourcePassCount: number;
  uniqueNodeCount: number;
  alreadyReadyNodes: number;
  asynchronouslyCompiledPipelines: number;
  pendingNodeReuses: number;
  yields: number;
} {
  return {
    commandCount,
    sourcePassCount,
    uniqueNodeCount,
    alreadyReadyNodes: 0,
    asynchronouslyCompiledPipelines: 0,
    pendingNodeReuses: 0,
    yields: 0,
  };
}

function freezePrewarmStats(
  stats: ReturnType<typeof mutablePrewarmStats>,
): ThreeWebGpuComputePrewarmStats {
  return Object.freeze({ ...stats });
}

function defaultPrewarmYield(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });
}

function rendererPendingNodePrewarms(renderer: object): WeakMap<object, Promise<void>> {
  let nodePrewarms = pendingNodePrewarms.get(renderer);
  if (!nodePrewarms) {
    nodePrewarms = new WeakMap<object, Promise<void>>();
    pendingNodePrewarms.set(renderer, nodePrewarms);
  }
  return nodePrewarms;
}

function installComputeNodeDisposeOwnership(
  renderer: any,
  surface: NonNullable<ReturnType<typeof privateSurface>>,
  node: any,
): void {
  if (surface.pipelines.has(node)) return;
  const dispose = (): void => {
    node.removeEventListener('dispose', dispose);
    surface.pipelines.delete(node);
    surface.bindings.deleteForCompute(node);
    surface.nodes.delete(node);
  };
  node.addEventListener('dispose', dispose);
  const onInitFn = node.onInitFunction;
  if (onInitFn !== null && onInitFn !== undefined) {
    onInitFn.call(node, { renderer });
  }
}

function updateComputeNodeForIsolatedPrewarm(
  surface: NonNullable<ReturnType<typeof privateSurface>>,
  node: any,
): any[] {
  const { nodeFrame } = surface;
  if (
    typeof nodeFrame.renderId !== 'number'
    || typeof nodeFrame.frameId !== 'number'
  ) {
    throw new Error('Three r183 NodeFrame render/frame ids are unavailable');
  }
  const previousRenderId = nodeFrame.renderId;
  const previousFrameId = nodeFrame.frameId;
  const isolatedId = nextPrewarmNodeFrameId--;
  try {
    nodeFrame.renderId = isolatedId;
    nodeFrame.frameId = isolatedId;
    surface.nodes.updateForCompute(node);
    surface.bindings.updateForCompute(node);
    return surface.bindings.getForCompute(node) as any[];
  } finally {
    nodeFrame.renderId = previousRenderId;
    nodeFrame.frameId = previousFrameId;
  }
}

/**
 * Materialize one Three r183 compute pipeline through Three's own cache and
 * ownership path, replacing only the final synchronous WebGPU pipeline call.
 * The monkey patch is installed and restored in one synchronous turn; the
 * awaited compilation begins only after the original backend method is back.
 */
async function materializeComputeNodePipelineAsync(
  renderer: any,
  surface: NonNullable<ReturnType<typeof privateSurface>>,
  node: any,
): Promise<void> {
  if (isThreeWebGpuComputeBridgeReady(renderer, [node])) return;

  const backend = surface.backend;
  const device = backend?.device as GPUDevice | undefined;
  if (
    !device
    || typeof device.createPipelineLayout !== 'function'
    || typeof device.createComputePipelineAsync !== 'function'
    || typeof backend.createComputePipeline !== 'function'
  ) {
    throw new Error('Three r183 async WebGPU compute-pipeline surface is unavailable');
  }

  installComputeNodeDisposeOwnership(renderer, surface, node);
  const computeBindings = updateComputeNodeForIsolatedPrewarm(surface, node);

  if (surface.pipelines.has(node)) {
    const existingWrapper = surface.pipelines.get(node)?.pipeline;
    if (existingWrapper && backend.get(existingWrapper)?.pipeline) return;
    throw new Error('Three compute cache contains a pipeline wrapper with no native pipeline or tracked prewarm');
  }

  if (interceptedComputePipelineBackends.has(backend)) {
    throw new Error('Three compute-pipeline async interception was re-entered');
  }

  const hadOwnCreatePipeline = Object.prototype.hasOwnProperty.call(
    backend,
    'createComputePipeline',
  );
  const ownCreatePipelineDescriptor = hadOwnCreatePipeline
    ? Object.getOwnPropertyDescriptor(backend, 'createComputePipeline')
    : undefined;
  let capturedPipelineWrapper: any = null;
  let compilationPromise: Promise<GPUComputePipeline> | null = null;
  let pipelineWrapper: any = null;
  let materializationError: unknown = null;

  const interceptedCreateComputePipeline = (
    computePipeline: any,
    bindings: any[],
  ): void => {
    if (compilationPromise !== null) {
      throw new Error('Three requested more than one compute pipeline for one ComputeNode');
    }
    const computeProgram = backend.get(computePipeline?.computeProgram)?.module;
    if (!computeProgram) {
      throw new Error('Three native compute shader module is unavailable');
    }
    const bindGroupLayouts = bindings.map((binding) => {
      const layoutGPU = backend.get(binding)?.layout?.layoutGPU as GPUBindGroupLayout | undefined;
      if (!layoutGPU) throw new Error('Three native compute bind-group layout is unavailable');
      return layoutGPU;
    });
    const pipelineData = backend.get(computePipeline);
    if (!pipelineData) throw new Error('Three native compute pipeline data is unavailable');

    capturedPipelineWrapper = computePipeline;
    // This descriptor deliberately mirrors Three r183's
    // WebGPUPipelineUtils.createComputePipeline() byte-for-byte in shape.
    const descriptor: GPUComputePipelineDescriptor = {
      compute: computeProgram,
      layout: device.createPipelineLayout({ bindGroupLayouts }),
    };
    compilationPromise = device.createComputePipelineAsync(descriptor).then((pipeline) => {
      pipelineData.pipeline = pipeline;
      return pipeline;
    });
  };

  interceptedComputePipelineBackends.add(backend);
  try {
    try {
      Object.defineProperty(backend, 'createComputePipeline', {
        configurable: true,
        writable: true,
        value: interceptedCreateComputePipeline,
      });
      pipelineWrapper = surface.pipelines.getForCompute(node, computeBindings);
    } catch (error) {
      materializationError = error;
    }
  } finally {
    try {
      try {
        if (hadOwnCreatePipeline && ownCreatePipelineDescriptor) {
          Object.defineProperty(
            backend,
            'createComputePipeline',
            ownCreatePipelineDescriptor,
          );
        } else {
          delete backend.createComputePipeline;
        }
      } catch (error) {
        if (materializationError === null) materializationError = error;
      }
    } finally {
      interceptedComputePipelineBackends.delete(backend);
    }
  }

  if (materializationError !== null) {
    // The private call failed after it may already have launched async device
    // work. Attach a rejection handler before surfacing the ABI failure so the
    // abandoned compilation can never become an unhandled rejection.
    const abandonedCompilation = compilationPromise as Promise<GPUComputePipeline> | null;
    if (abandonedCompilation !== null) void abandonedCompilation.catch(() => undefined);
    throw materializationError;
  }

  const alreadyMaterialized = pipelineWrapper
    ? backend.get(pipelineWrapper)?.pipeline as GPUComputePipeline | undefined
    : undefined;
  if (alreadyMaterialized) return;
  if (
    !pipelineWrapper
    || pipelineWrapper !== capturedPipelineWrapper
    || compilationPromise === null
  ) {
    throw new Error('Three did not expose the expected async compute-pipeline materialization');
  }
  await compilationPromise;
  if (!backend.get(pipelineWrapper)?.pipeline) {
    throw new Error('Three async compute pipeline resolved without publishing its native pipeline');
  }
}

async function ensureComputeNodePrewarmed(
  renderer: any,
  surface: NonNullable<ReturnType<typeof privateSurface>>,
  node: any,
): Promise<AsyncNodePrewarmOutcome> {
  if (isThreeWebGpuComputeBridgeReady(renderer, [node])) return 'ready';
  const rendererKey = renderer as object;
  const nodeKey = node as object;
  const nodePrewarms = rendererPendingNodePrewarms(rendererKey);
  const pending = nodePrewarms.get(nodeKey);
  if (pending) {
    await pending;
    if (!isThreeWebGpuComputeBridgeReady(renderer, [node])) {
      throw new Error('shared Three compute-pipeline prewarm resolved without a ready pipeline');
    }
    return 'pending-reuse';
  }

  const created = materializeComputeNodePipelineAsync(renderer, surface, node);
  nodePrewarms.set(nodeKey, created);
  try {
    await created;
  } finally {
    if (nodePrewarms.get(nodeKey) === created) nodePrewarms.delete(nodeKey);
  }
  if (!isThreeWebGpuComputeBridgeReady(renderer, [node])) {
    throw new Error('Three compute-pipeline prewarm completed without a ready pipeline');
  }
  return 'compiled';
}

/**
 * Asynchronously prewarm the exact compute pipelines later consumed by
 * prepareThreeWebGpuCompute(). No command encoder, compute pass, or queue
 * submission is created here. Failures are returned explicitly so callers can
 * keep physics gated rather than entering Three with a half-built cache.
 */
export async function prewarmThreeWebGpuCompute(
  renderer: any,
  commands: readonly ThreeWebGpuComputeCommand[],
  options?: ThreeWebGpuComputePrewarmOptions,
): Promise<ThreeWebGpuComputePrewarmResult> {
  let sourcePassCount = 0;
  let currentPassIndex: number | null = null;
  const uniqueNodes: any[] = [];
  const seenNodes = new Set<any>();
  let validationFailure: string | null = null;
  const repeatedNodeInvariant = new Map<any, boolean>();
  for (const command of commands) {
    if (currentPassIndex !== command.passIndex) {
      currentPassIndex = command.passIndex;
      sourcePassCount++;
    }
    if (command.node?.isComputeNode !== true) {
      validationFailure = 'a command is not a Three ComputeNode';
      break;
    }
    if (!Number.isSafeInteger(command.passIndex) || command.passIndex < 0) {
      validationFailure = 'a command has an invalid compute pass index';
      break;
    }
    const earlierInvariant = repeatedNodeInvariant.get(command.node);
    if (
      earlierInvariant !== undefined
      && (!earlierInvariant || command.uniformStateInvariant !== true)
    ) {
      validationFailure = 'a repeated Three ComputeNode lacks an invariant-uniform proof';
      break;
    }
    repeatedNodeInvariant.set(command.node, command.uniformStateInvariant === true);
    if (!seenNodes.has(command.node)) {
      seenNodes.add(command.node);
      uniqueNodes.push(command.node);
    }
  }
  const stats = mutablePrewarmStats(
    commands.length,
    sourcePassCount,
    uniqueNodes.length,
  );
  const fail = (reason: string): ThreeWebGpuComputePrewarmFailure => ({
    ok: false,
    reason,
    stats: freezePrewarmStats(stats),
  });

  if (String(THREE_REVISION) !== THREE_WEBGPU_COMPUTE_BRIDGE_REVISION) {
    return fail(
      `unsupported Three.js revision ${String(THREE_REVISION)} (expected ${THREE_WEBGPU_COMPUTE_BRIDGE_REVISION})`,
    );
  }
  if (commands.length === 0) return fail('no compute commands were supplied');
  if (validationFailure) return fail(validationFailure);
  const forcedFallback = renderer?.[PMAVBD_FORCE_RENDERER_COMPUTE_FALLBACK];
  if (forcedFallback) return fail('renderer fallback was forced for regression');
  const surface = privateSurface(renderer);
  if (!surface) return fail('Three r183 private WebGPU compute surface is unavailable');

  const yieldEveryNodes = Math.max(0, Math.floor(options?.yieldEveryNodes ?? 1));
  const yieldToMainThread = options?.yieldToMainThread ?? defaultPrewarmYield;
  try {
    const batchSize = yieldEveryNodes > 0
      ? yieldEveryNodes
      : Math.max(1, uniqueNodes.length);
    const pendingOutcomes: Promise<void>[] = [];
    let firstError: unknown = null;
    let completedNodes = 0;
    for (let batchStart = 0; batchStart < uniqueNodes.length; batchStart += batchSize) {
      const batchEnd = Math.min(uniqueNodes.length, batchStart + batchSize);
      // materializeComputeNodePipelineAsync restores Three's temporary backend
      // interception before its first await. Schedule a bounded amount of graph
      // setup per browser frame, but do not wait for the previous native batch:
      // keeping all driver workers fed avoids serial batch-tail latency while
      // the rAF yield keeps scene/status/input painting normally.
      for (const node of uniqueNodes.slice(batchStart, batchEnd)) {
        pendingOutcomes.push(
          ensureComputeNodePrewarmed(renderer, surface, node).then((outcome) => {
            if (outcome === 'ready') stats.alreadyReadyNodes++;
            else if (outcome === 'compiled') stats.asynchronouslyCompiledPipelines++;
            else stats.pendingNodeReuses++;
            completedNodes++;
            options?.onProgress?.(completedNodes, uniqueNodes.length);
          }).catch((error: unknown) => {
            // Attach the rejection sink when the native compilation is
            // launched, not several rAF batches later. Otherwise a fast
            // driver rejection (or a throwing progress callback) can be
            // reported as unhandled before the final Promise.all installs its
            // observer.
            if (firstError === null) firstError = error;
          }),
        );
      }

      if (yieldEveryNodes > 0 && batchEnd < uniqueNodes.length) {
        await yieldToMainThread();
        stats.yields++;
      }
    }
    await Promise.all(pendingOutcomes);
    if (firstError !== null) throw firstError;
  } catch (error) {
    return fail(failureReason(error));
  }

  return {
    ok: true,
    stats: freezePrewarmStats(stats),
  };
}

function finiteWorkgroupCount(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.floor(value))
    : fallback;
}

/**
 * Pure dispatch normalizer mirroring Three r183's WebGPUBackend.compute().
 * Exported so GPU-free source/self-tests can cover number and [x,y,z] paths.
 */
export function resolveThreeWebGpuDirectDispatchForTesting(
  dispatchSize: number | readonly [number, number?, number?],
  workgroupSize: readonly number[],
  maxComputeWorkgroupsPerDimension: number,
): DirectDispatch {
  if (typeof dispatchSize === 'number') {
    const threadsPerWorkgroup = Math.max(
      1,
      workgroupSize.reduce(
        (product, value) => product * Math.max(1, finiteWorkgroupCount(value, 1)),
        1,
      ),
    );
    const dispatchCount = Math.ceil(Math.max(0, dispatchSize) / threadsPerWorkgroup);
    const maxDimension = Math.max(1, finiteWorkgroupCount(maxComputeWorkgroupsPerDimension, 65535));
    return {
      kind: 'direct',
      x: Math.min(dispatchCount, maxDimension),
      y: dispatchCount > maxDimension ? Math.ceil(dispatchCount / maxDimension) : 1,
      z: 1,
    };
  }

  return {
    kind: 'direct',
    x: finiteWorkgroupCount(dispatchSize[0], 0),
    // Three r183's WebGPU backend resolves explicit vector dispatches as
    // `dispatchSize[1] || 1` / `dispatchSize[2] || 1`. Preserve that ABI for
    // zero as well as for omitted axes; forwarding a literal zero here would
    // silently turn an otherwise valid x-only dispatch into zero work.
    y: finiteWorkgroupCount(dispatchSize[1], 1) || 1,
    z: finiteWorkgroupCount(dispatchSize[2], 1) || 1,
  };
}

function restoreAccounting(renderer: any, surface: NonNullable<ReturnType<typeof privateSurface>>, snapshot: RendererAccountingSnapshot): void {
  renderer.info.calls = snapshot.calls;
  renderer.info.compute.calls = snapshot.computeCalls;
  renderer.info.compute.frameCalls = snapshot.computeFrameCalls;
  surface.nodeFrame.renderId = snapshot.renderId;
}

/**
 * Resolve all native pipelines, bind groups and dispatches before a caller
 * starts encoding. For the pinned r183 ABI this also performs Three's exact
 * per-node onInit/pipeline/binding materialization without beginning or
 * submitting a compute pass, so the first physical step can use the same
 * bounded native command buffer as every later step.
 */
export function prepareThreeWebGpuCompute(
  renderer: any,
  commands: readonly ThreeWebGpuComputeCommand[],
  options?: ThreeWebGpuComputePrepareOptions,
): ThreeWebGpuComputePrepareResult {
  if (String(THREE_REVISION) !== THREE_WEBGPU_COMPUTE_BRIDGE_REVISION) {
    return {
      ok: false,
      reason: `unsupported Three.js revision ${String(THREE_REVISION)} (expected ${THREE_WEBGPU_COMPUTE_BRIDGE_REVISION})`,
    };
  }
  if (commands.length === 0) {
    return { ok: false, reason: 'no compute commands were supplied' };
  }
  const forcedFallback = renderer?.[PMAVBD_FORCE_RENDERER_COMPUTE_FALLBACK];
  if (forcedFallback) {
    if (typeof forcedFallback === 'function') forcedFallback();
    return { ok: false, reason: 'renderer fallback was forced for regression' };
  }
  const surface = privateSurface(renderer);
  if (!surface) {
    return { ok: false, reason: 'Three r183 private WebGPU compute surface is unavailable' };
  }
  const repeatedNodeInvariant = new Map<any, boolean>();
  for (const command of commands) {
    if (command.node?.isComputeNode !== true) {
      return { ok: false, reason: 'a command is not a Three ComputeNode' };
    }
    if (!Number.isSafeInteger(command.passIndex) || command.passIndex < 0) {
      return { ok: false, reason: 'a command has an invalid compute pass index' };
    }
    const earlierInvariant = repeatedNodeInvariant.get(command.node);
    if (
      earlierInvariant !== undefined
      && (!earlierInvariant || command.uniformStateInvariant !== true)
    ) {
      return {
        ok: false,
        reason: 'a repeated Three ComputeNode lacks an invariant-uniform proof',
      };
    }
    repeatedNodeInvariant.set(command.node, command.uniformStateInvariant === true);
  }

  const info = renderer?.info;
  if (
    !info
    || typeof info.calls !== 'number'
    || typeof info.compute?.calls !== 'number'
    || typeof info.compute?.frameCalls !== 'number'
  ) {
    return { ok: false, reason: 'Three renderer info accounting is unavailable' };
  }
  const accounting: RendererAccountingSnapshot = {
    calls: info.calls,
    computeCalls: info.compute.calls,
    computeFrameCalls: info.compute.frameCalls,
    renderId: surface.nodeFrame.renderId,
  };
  let accountingLive = true;
  const rollbackAccounting = (): void => {
    if (!accountingLive) return;
    accountingLive = false;
    restoreAccounting(renderer, surface, accounting);
  };

  try {
    // Match every original WebGPURenderer.compute() call, not merely the one
    // queue submit that will carry all passes. A distinct passIndex advances
    // info/renderId before node updates just as separate renderer calls do.
    // The pre-existing broadphase AABB bridge opts out because it was never a
    // renderer-level compute call; it still receives a fresh temporary id so
    // per-render node updates remain correct.
    const accountRendererCall = options?.accountRendererCall ?? true;
    const prepared = options?.preparationCache?.commands ?? [];
    let preparedCommandCount = 0;
    const invariantNodePreparation = new Map<any, Readonly<{
      pipeline: GPUComputePipeline;
      bindGroups: readonly GPUBindGroup[];
    }>>();
    let sourcePassCount = 0;
    let nodePreparations = 0;
    let invariantNodeReuses = 0;
    const maxDimension = Number(
      surface.backend?.device?.limits?.maxComputeWorkgroupsPerDimension
      ?? renderer?.backend?.device?.limits?.maxComputeWorkgroupsPerDimension
      ?? 65535,
    );
    let currentPassIndex: number | null = null;
    for (const command of commands) {
      if (currentPassIndex !== command.passIndex) {
        currentPassIndex = command.passIndex;
        sourcePassCount++;
        if (accountRendererCall) {
          info.calls++;
          info.compute.calls++;
          info.compute.frameCalls++;
          surface.nodeFrame.renderId = info.calls;
        } else {
          surface.nodeFrame.renderId = info.calls + 1;
        }
      }
      const node = command.node;
      const cachedPreparation = command.uniformStateInvariant === true
        ? invariantNodePreparation.get(node)
        : undefined;
      let pipeline: GPUComputePipeline;
      let bindGroups: readonly GPUBindGroup[];
      if (cachedPreparation) {
        pipeline = cachedPreparation.pipeline;
        bindGroups = cachedPreparation.bindGroups;
        invariantNodeReuses++;
      } else {
        if (!surface.pipelines.has(node)) {
        // Mirror Renderer.compute()'s r183 first-use lifecycle, but stop before
        // backend.beginCompute/backend.compute. Pipeline and bind-group
        // materialization are CPU/backend setup; no queue submission is
        // required, and the node retains the same disposal ownership that
        // Three would install during its first renderer.compute() call.
          const dispose = (): void => {
            node.removeEventListener('dispose', dispose);
            surface.pipelines.delete(node);
            surface.bindings.deleteForCompute(node);
            surface.nodes.delete(node);
          };
          node.addEventListener('dispose', dispose);
          const onInitFn = node.onInitFunction;
          if (onInitFn !== null && onInitFn !== undefined) {
            onInitFn.call(node, { renderer });
          }
        }
        surface.nodes.updateForCompute(node);
        surface.bindings.updateForCompute(node);
        const computeBindings = surface.bindings.getForCompute(node) as any[];
        const computePipeline = surface.pipelines.getForCompute(node, computeBindings);
        const resolvedPipeline = surface.backend.get(computePipeline)?.pipeline as GPUComputePipeline | undefined;
        if (!resolvedPipeline) throw new Error('native Three compute pipeline is unavailable');

        const resolvedBindGroups: GPUBindGroup[] = [];
        for (const binding of computeBindings) {
          const group = surface.backend.get(binding)?.group as GPUBindGroup | undefined;
          if (!group) throw new Error('native Three compute bind group is unavailable');
          resolvedBindGroups.push(group);
        }
        pipeline = resolvedPipeline;
        bindGroups = resolvedBindGroups;
        nodePreparations++;
        if (command.uniformStateInvariant === true) {
          invariantNodePreparation.set(node, { pipeline, bindGroups });
        }
      }

      const requestedDispatch = command.dispatchSize ?? node.count;
      let dispatch: PreparedDispatch;
      if (
        requestedDispatch
        && typeof requestedDispatch === 'object'
        && !Array.isArray(requestedDispatch)
        && (requestedDispatch as any).isIndirectStorageBufferAttribute === true
      ) {
        const buffer = surface.backend.get(requestedDispatch)?.buffer as GPUBuffer | undefined;
        if (!buffer) throw new Error('native Three indirect dispatch buffer is unavailable');
        dispatch = { kind: 'indirect', buffer };
      } else if (typeof requestedDispatch === 'number' || Array.isArray(requestedDispatch)) {
        dispatch = resolveThreeWebGpuDirectDispatchForTesting(
          requestedDispatch as number | readonly [number, number?, number?],
          Array.isArray(node.workgroupSize) ? node.workgroupSize : [1, 1, 1],
          maxDimension,
        );
      } else {
        throw new Error('unsupported Three compute dispatch size');
      }
      const cachedCommand = prepared[preparedCommandCount];
      if (cachedCommand) {
        cachedCommand.pipeline = pipeline;
        cachedCommand.bindGroups = bindGroups;
        cachedCommand.dispatch = dispatch;
        cachedCommand.passIndex = command.passIndex;
      } else {
        prepared.push({ pipeline, bindGroups, dispatch, passIndex: command.passIndex });
      }
      preparedCommandCount++;
    }
    prepared.length = preparedCommandCount;

    // Three restores renderId but keeps its call counters after compute().
    surface.nodeFrame.renderId = accounting.renderId;
    accountingLive = false;
    let committedAccounting = true;
    return {
      ok: true,
      commands: prepared,
      stats: {
        commandCount: prepared.length,
        sourcePassCount,
        nodePreparations,
        invariantNodeReuses,
      },
      rollbackAccounting: () => {
        if (!committedAccounting) return;
        committedAccounting = false;
        restoreAccounting(renderer, surface, accounting);
      },
    };
  } catch (error) {
    rollbackAccounting();
    return { ok: false, reason: failureReason(error) };
  }
}

export type ThreeWebGpuComputeRecordStats = Readonly<{
  commandCount: number;
  sourcePassCount: number;
  encodedPassCount: number;
  pipelineSets: number;
  pipelineSetSkips: number;
  bindGroupSets: number;
  bindGroupSetSkips: number;
}>;

/**
 * Record ordered compute work into one caller-owned command buffer. Native
 * production recording coalesces renderer-level pass boundaries: those
 * boundaries have already served their CPU accounting/update purpose during
 * preparation, while WebGPU dispatch boundaries provide the storage ordering
 * required by the graph. A faithful mode remains available to the self-test.
 */
export function recordPreparedThreeWebGpuCompute(
  commandEncoder: GPUCommandEncoder,
  prepared: PreparedThreeWebGpuCompute,
  label: string,
  options?: Readonly<{
    coalescePasses?: boolean;
    computePassDescriptorFactory?: NativeComputePassDescriptorFactory;
  }>,
): ThreeWebGpuComputeRecordStats {
  let pass: GPUComputePassEncoder | null = null;
  let currentPassIndex: number | null = null;
  let encodedPassCount = 0;
  let pipelineSets = 0;
  let pipelineSetSkips = 0;
  let bindGroupSets = 0;
  let bindGroupSetSkips = 0;
  let currentPipeline: GPUComputePipeline | null = null;
  const currentBindGroups: Array<GPUBindGroup | undefined> = [];
  const coalescePasses = options?.coalescePasses ?? true;
  const endPass = (): void => {
    const ending = pass;
    pass = null;
    currentPassIndex = null;
    currentPipeline = null;
    currentBindGroups.length = 0;
    ending?.end();
  };
  try {
    for (const command of prepared.commands) {
      if (
        pass === null
        || (!coalescePasses && currentPassIndex !== command.passIndex)
      ) {
        if (pass !== null) endPass();
        currentPassIndex = command.passIndex;
        encodedPassCount++;
        const passLabel = coalescePasses
          ? `${label} [coalesced ${prepared.stats.sourcePassCount} passes]`
          : `${label} [pass ${command.passIndex}]`;
        const requestedDescriptor = options?.computePassDescriptorFactory?.(passLabel);
        pass = commandEncoder.beginComputePass({
          ...requestedDescriptor,
          label: requestedDescriptor?.label ?? passLabel,
        });
      }
      if (currentPipeline !== command.pipeline) {
        pass.setPipeline(command.pipeline);
        currentPipeline = command.pipeline;
        pipelineSets++;
      } else {
        pipelineSetSkips++;
      }
      for (let groupIndex = 0; groupIndex < command.bindGroups.length; groupIndex++) {
        const bindGroup = command.bindGroups[groupIndex];
        if (currentBindGroups[groupIndex] !== bindGroup) {
          pass.setBindGroup(groupIndex, bindGroup);
          currentBindGroups[groupIndex] = bindGroup;
          bindGroupSets++;
        } else {
          bindGroupSetSkips++;
        }
      }
      if (command.dispatch.kind === 'indirect') {
        pass.dispatchWorkgroupsIndirect(command.dispatch.buffer, 0);
      } else {
        pass.dispatchWorkgroups(command.dispatch.x, command.dispatch.y, command.dispatch.z);
      }
    }
  } finally {
    if (pass !== null) endPass();
  }
  return {
    commandCount: prepared.commands.length,
    sourcePassCount: prepared.stats.sourcePassCount,
    encodedPassCount,
    pipelineSets,
    pipelineSetSkips,
    bindGroupSets,
    bindGroupSetSkips,
  };
}

export type ThreeWebGpuComputeBridgeSelfTestResult = Readonly<{
  ok: boolean;
  checks: number;
  failures: readonly string[];
}>;

/** Deterministic GPU-free contract checks for the version lock and dispatch ABI. */
export function runThreeWebGpuComputeBridgeSelfTest(): ThreeWebGpuComputeBridgeSelfTestResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };

  check(
    'bridge is locked to installed Three revision',
    String(THREE_REVISION) === THREE_WEBGPU_COMPUTE_BRIDGE_REVISION,
  );
  const numeric = resolveThreeWebGpuDirectDispatchForTesting(513, [256, 1, 1], 65535);
  check('numeric count is divided by workgroup size', numeric.x === 3 && numeric.y === 1 && numeric.z === 1);
  const split = resolveThreeWebGpuDirectDispatchForTesting(17, [1], 8);
  check('numeric count splits across the device x limit', split.x === 8 && split.y === 3 && split.z === 1);
  const vector = resolveThreeWebGpuDirectDispatchForTesting([7, 5, 3], [64, 1, 1], 65535);
  check('explicit [x,y,z] is already a workgroup count', vector.x === 7 && vector.y === 5 && vector.z === 3);
  const zeroSecondaryAxes = resolveThreeWebGpuDirectDispatchForTesting([7, 0, 0], [64, 1, 1], 65535);
  check(
    'explicit zero y/z axes match Three r183 fallback-to-one semantics',
    zeroSecondaryAxes.x === 7 && zeroSecondaryAxes.y === 1 && zeroSecondaryAxes.z === 1,
  );

  const indirect = { isIndirectStorageBufferAttribute: true as const };
  const node = { isComputeNode: true, count: indirect, workgroupSize: [64, 1, 1] };
  const pipelineToken = {};
  const bindingToken = {};
  const pipeline = {} as GPUComputePipeline;
  const group = {} as GPUBindGroup;
  const buffer = {} as GPUBuffer;
  const renderer: any = {
    info: { calls: 4, compute: { calls: 2, frameCalls: 1 } },
    backend: {
      device: { limits: { maxComputeWorkgroupsPerDimension: 65535 } },
      get(value: unknown): unknown {
        if (value === pipelineToken) return { pipeline };
        if (value === bindingToken) return { group };
        if (value === indirect) return { buffer };
        return undefined;
      },
    },
    _nodes: {
      nodeFrame: { renderId: 9 },
      updateForCompute(): void {},
    },
    _bindings: {
      updateForCompute(): void {},
      getForCompute(): unknown[] { return [bindingToken]; },
    },
    _pipelines: {
      has(value: unknown): boolean { return value === node; },
      get(value: unknown): object { return value === node ? { pipeline: pipelineToken } : {}; },
      getForCompute(): object { return pipelineToken; },
    },
  };
  const prepared = prepareThreeWebGpuCompute(renderer, [{ node, passIndex: 0 }]);
  check(
    'indirect attribute resolves to its native GPU buffer',
    prepared.ok
      && prepared.commands[0]?.dispatch.kind === 'indirect'
      && prepared.commands[0].dispatch.buffer === buffer,
  );
  check(
    'native preparation retains the original pass index',
    prepared.ok && prepared.commands[0]?.passIndex === 0,
  );
  check(
    'native preparation mirrors call accounting and restores render id',
    prepared.ok
      && renderer.info.calls === 5
      && renderer.info.compute.calls === 3
      && renderer.info.compute.frameCalls === 2
      && renderer._nodes.nodeFrame.renderId === 9,
  );
  if (prepared.ok) prepared.rollbackAccounting();
  check(
    'abandoned recording rolls renderer accounting back exactly',
    renderer.info.calls === 4
      && renderer.info.compute.calls === 2
      && renderer.info.compute.frameCalls === 1
      && renderer._nodes.nodeFrame.renderId === 9,
  );

  const nativeGet = renderer.backend.get;
  renderer.backend.get = (value: unknown): unknown => (
    value === indirect ? undefined : nativeGet.call(renderer.backend, value)
  );
  const missingIndirect = prepareThreeWebGpuCompute(renderer, [{ node, passIndex: 0 }]);
  check(
    'missing indirect native buffer fails closed',
    !missingIndirect.ok && missingIndirect.reason.includes('indirect dispatch buffer'),
  );
  check(
    'failed indirect preparation cannot leak renderer accounting',
    renderer.info.calls === 4
      && renderer.info.compute.calls === 2
      && renderer.info.compute.frameCalls === 1
      && renderer._nodes.nodeFrame.renderId === 9,
  );
  renderer.backend.get = nativeGet;

  const repeatedWithoutProof = prepareThreeWebGpuCompute(renderer, [
    { node, passIndex: 0 },
    { node, passIndex: 1 },
  ]);
  check(
    'repeated mutable nodes fail closed without an invariant-uniform proof',
    !repeatedWithoutProof.ok && repeatedWithoutProof.reason.includes('invariant-uniform'),
  );
  const repeatedWithProof = prepareThreeWebGpuCompute(renderer, [
    { node, passIndex: 0, uniformStateInvariant: true },
    { node, passIndex: 1, uniformStateInvariant: true },
  ]);
  check(
    'an explicit invariant-uniform proof permits repeated native nodes',
    repeatedWithProof.ok
      && repeatedWithProof.commands.length === 2
      && repeatedWithProof.stats.nodePreparations === 1
      && repeatedWithProof.stats.invariantNodeReuses === 1,
  );
  if (repeatedWithProof.ok) repeatedWithProof.rollbackAccounting();

  const preparationCache = createThreeWebGpuComputePreparationCache();
  const cachedFirst = prepareThreeWebGpuCompute(
    renderer,
    [{ node, passIndex: 0, dispatchSize: [1, 1, 1] }],
    { preparationCache },
  );
  const cachedCommandIdentity = cachedFirst.ok ? cachedFirst.commands[0] : null;
  if (cachedFirst.ok) cachedFirst.rollbackAccounting();
  const cachedSecond = prepareThreeWebGpuCompute(
    renderer,
    [{ node, passIndex: 0, dispatchSize: [3, 1, 1] }],
    { preparationCache },
  );
  check(
    'preparation cache reuses command storage while refreshing dispatch state',
    cachedSecond.ok
      && cachedSecond.commands[0] === cachedCommandIdentity
      && cachedSecond.commands[0]?.dispatch.kind === 'direct'
      && cachedSecond.commands[0].dispatch.x === 3,
  );
  if (cachedSecond.ok) cachedSecond.rollbackAccounting();

  const passEvents: string[] = [];
  let activePass = -1;
  const fakeEncoder = {
    beginComputePass(descriptor: { label: string }): any {
      const match = descriptor.label.match(/\[pass (\d+)\]$/);
      activePass = Number(match?.[1] ?? -1);
      passEvents.push(`begin:${activePass}`);
      return {
        setPipeline(): void {},
        setBindGroup(): void {},
        dispatchWorkgroups(x: number): void { passEvents.push(`dispatch:${activePass}:${x}`); },
        dispatchWorkgroupsIndirect(): void { passEvents.push(`indirect:${activePass}`); },
        end(): void { passEvents.push(`end:${activePass}`); },
      };
    },
  } as GPUCommandEncoder;
  const multiPassPrepared: PreparedThreeWebGpuCompute = {
    ok: true,
    commands: [
      { pipeline, bindGroups: [group], dispatch: { kind: 'direct', x: 1, y: 1, z: 1 }, passIndex: 2 },
      { pipeline, bindGroups: [group], dispatch: { kind: 'direct', x: 2, y: 1, z: 1 }, passIndex: 2 },
      { pipeline, bindGroups: [group], dispatch: { kind: 'indirect', buffer }, passIndex: 7 },
      { pipeline, bindGroups: [group], dispatch: { kind: 'direct', x: 3, y: 1, z: 1 }, passIndex: 9 },
    ],
    stats: {
      commandCount: 4,
      sourcePassCount: 3,
      nodePreparations: 1,
      invariantNodeReuses: 3,
    },
    rollbackAccounting(): void {},
  };
  const coalescedStats = recordPreparedThreeWebGpuCompute(
    fakeEncoder,
    multiPassPrepared,
    'self-test',
  );
  check(
    'native recording coalesces source passes without changing command order',
    passEvents.join('|') === [
      'begin:-1',
      'dispatch:-1:1',
      'dispatch:-1:2',
      'indirect:-1',
      'dispatch:-1:3',
      'end:-1',
    ].join('|'),
  );
  check(
    'coalesced recording reports one pass and redundant state skips',
    coalescedStats.sourcePassCount === 3
      && coalescedStats.encodedPassCount === 1
      && coalescedStats.pipelineSets === 1
      && coalescedStats.pipelineSetSkips === 3
      && coalescedStats.bindGroupSets === 1
      && coalescedStats.bindGroupSetSkips === 3,
  );

  passEvents.length = 0;
  const timedPassLabels: string[] = [];
  recordPreparedThreeWebGpuCompute(
    fakeEncoder,
    multiPassPrepared,
    'self-test-timed',
    {
      computePassDescriptorFactory: (passLabel) => {
        timedPassLabels.push(passLabel);
        return { label: passLabel };
      },
    },
  );
  check(
    'native compute descriptor factory observes the one coalesced production pass',
    timedPassLabels.length === 1
      && timedPassLabels[0]?.includes('coalesced 3 passes') === true,
  );

  passEvents.length = 0;
  const faithfulStats = recordPreparedThreeWebGpuCompute(
    fakeEncoder,
    multiPassPrepared,
    'self-test-faithful',
    { coalescePasses: false },
  );
  check(
    'diagnostic faithful mode preserves source pass boundaries',
    faithfulStats.encodedPassCount === 3
      && passEvents.join('|') === [
        'begin:2',
        'dispatch:2:1',
        'dispatch:2:2',
        'end:2',
        'begin:7',
        'indirect:7',
        'end:7',
        'begin:9',
        'dispatch:9:3',
        'end:9',
      ].join('|'),
  );

  return Object.freeze({
    ok: failures.length === 0,
    checks,
    failures: Object.freeze(failures),
  });
}

/** GPU-free async checks for the r183 pipeline interception and pending ABI. */
export async function runThreeWebGpuComputeBridgeAsyncSelfTest(): Promise<ThreeWebGpuComputeBridgeSelfTestResult> {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };

  const node: any = {
    isComputeNode: true,
    count: 64,
    workgroupSize: [64, 1, 1],
    onInitFunction: null,
    addEventListener(): void {},
    removeEventListener(): void {},
  };
  const computeProgram = {};
  const computeProgramGPU = { module: {}, entryPoint: 'main' };
  const pipelineWrapper = { computeProgram };
  const pipelineData: { pipeline?: GPUComputePipeline } = {};
  const binding = {};
  const bindGroupLayout = {} as GPUBindGroupLayout;
  const bindGroup = {} as GPUBindGroup;
  const pipelineLayout = {} as GPUPipelineLayout;
  const nativePipeline = {} as GPUComputePipeline;
  let resolveCompilation: ((pipeline: GPUComputePipeline) => void) | null = null;
  const compilationGate = new Promise<GPUComputePipeline>((resolve) => {
    resolveCompilation = resolve;
  });
  let asyncPipelineCalls = 0;
  let syncPipelineCalls = 0;
  let pipelineCachePresent = false;
  let observedRenderId = 0;
  let observedFrameId = 0;
  let capturedDescriptor: GPUComputePipelineDescriptor | null = null;
  const originalCreateComputePipeline = (): void => {
    syncPipelineCalls++;
  };
  const renderer: any = {
    info: { calls: 3, compute: { calls: 2, frameCalls: 1 } },
    backend: {
      device: {
        limits: { maxComputeWorkgroupsPerDimension: 65535 },
        createPipelineLayout(descriptor: GPUPipelineLayoutDescriptor): GPUPipelineLayout {
          check(
            'async prewarm forwards the exact Three bind-group layout',
            Array.from(descriptor.bindGroupLayouts)[0] === bindGroupLayout,
          );
          return pipelineLayout;
        },
        createComputePipelineAsync(
          descriptor: GPUComputePipelineDescriptor,
        ): Promise<GPUComputePipeline> {
          asyncPipelineCalls++;
          capturedDescriptor = descriptor;
          return compilationGate;
        },
      },
      createComputePipeline: originalCreateComputePipeline,
      get(value: unknown): unknown {
        if (value === computeProgram) return { module: computeProgramGPU };
        if (value === pipelineWrapper) return pipelineData;
        if (value === binding) return { layout: { layoutGPU: bindGroupLayout }, group: bindGroup };
        return undefined;
      },
    },
    _nodes: {
      nodeFrame: { renderId: 7, frameId: 11 },
      updateForCompute(): void {
        observedRenderId = renderer._nodes.nodeFrame.renderId;
        observedFrameId = renderer._nodes.nodeFrame.frameId;
      },
      delete(): void {},
    },
    _bindings: {
      updateForCompute(): void {},
      getForCompute(): unknown[] { return [binding]; },
      deleteForCompute(): void {},
    },
    _pipelines: {
      has(value: unknown): boolean { return value === node && pipelineCachePresent; },
      get(value: unknown): object {
        return value === node && pipelineCachePresent ? { pipeline: pipelineWrapper } : {};
      },
      getForCompute(value: unknown, bindings: unknown[]): object {
        if (value !== node) throw new Error('unexpected async self-test node');
        if (!pipelineCachePresent) {
          pipelineCachePresent = true;
          renderer.backend.createComputePipeline(pipelineWrapper, bindings);
        }
        return pipelineWrapper;
      },
      delete(): void { pipelineCachePresent = false; },
    },
  };
  const commands: ThreeWebGpuComputeCommand[] = [{ node, passIndex: 0 }];
  const first = prewarmThreeWebGpuCompute(renderer, commands, { yieldEveryNodes: 0 });
  const backendWasRestoredBeforeResolution =
    renderer.backend.createComputePipeline === originalCreateComputePipeline;
  const second = prewarmThreeWebGpuCompute(renderer, commands, { yieldEveryNodes: 0 });
  const resolveCompilationNow = resolveCompilation as ((pipeline: GPUComputePipeline) => void) | null;
  if (resolveCompilationNow) resolveCompilationNow(nativePipeline);
  const [firstResult, secondResult] = await Promise.all([first, second]);
  const observedDescriptor = capturedDescriptor as GPUComputePipelineDescriptor | null;

  check(
    'async prewarm uses createComputePipelineAsync exactly once',
    firstResult.ok && asyncPipelineCalls === 1 && syncPipelineCalls === 0,
  );
  check(
    'async prewarm restores the backend monkey patch before awaiting compilation',
    backendWasRestoredBeforeResolution
      && renderer.backend.createComputePipeline === originalCreateComputePipeline,
  );
  check(
    'async prewarm mirrors the r183 compute descriptor',
    observedDescriptor?.compute === computeProgramGPU
      && observedDescriptor?.layout === pipelineLayout,
  );
  check(
    'async prewarm publishes the resolved native pipeline into Three cache data',
    pipelineData.pipeline === nativePipeline
      && isThreeWebGpuComputeBridgeReady(renderer, [node]),
  );
  check(
    'async prewarm isolates node updates with negative ids and restores NodeFrame',
    observedRenderId < 0
      && observedFrameId === observedRenderId
      && renderer._nodes.nodeFrame.renderId === 7
      && renderer._nodes.nodeFrame.frameId === 11,
  );
  check(
    'concurrent prewarm callers share the renderer-scoped pending node promise',
    firstResult.ok
      && firstResult.stats.asynchronouslyCompiledPipelines === 1
      && secondResult.ok
      && secondResult.stats.pendingNodeReuses === 1,
  );

  renderer[PMAVBD_FORCE_RENDERER_COMPUTE_FALLBACK] = true;
  const forcedFailure = await prewarmThreeWebGpuCompute(renderer, commands, {
    yieldEveryNodes: 0,
  });
  delete renderer[PMAVBD_FORCE_RENDERER_COMPUTE_FALLBACK];
  check(
    'async prewarm reports forced/failing paths explicitly',
    !forcedFailure.ok && forcedFailure.reason.includes('forced'),
  );

  return Object.freeze({
    ok: failures.length === 0,
    checks,
    failures: Object.freeze(failures),
  });
}
